import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isFetchError } from '../utils/errorHandler';
import { setAuthContextRef, interceptedFetch } from '../utils/httpInterceptor';

export interface User {
  id: number;
  login: string;
  firstName: string;
  lastName: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (login: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => void;
  updateToken: (token: string) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (login: string, password: string, captchaToken?: string) => {
    try {
      setIsLoading(true);
      const requestBody: any = { login, password };
      if (captchaToken) {
        requestBody.captchaToken = captchaToken;
      }
      
      const response = await interceptedFetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle rate limiting with user-friendly message
        if (response.status === 429) {
          throw new Error('Too many login attempts. Please wait a few minutes before trying again.');
        }
        
        // Check if the error is the specific invalid credentials error code
        if (errorData.message === 'INVALID_CREDENTIALS') {
          throw new Error(t('auth.invalidCredentials'));
        }
        
        throw new Error(errorData.message || t('auth.invalidCredentials'));
      }

      const data = await response.json();
      
      setToken(data.access_token);
      setUser(data.user);
      
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('authUser', JSON.stringify(data.user));
    } catch (error) {
      console.error('Login error:', error);
      
      if (isFetchError(error)) {
        throw new Error(t('errors.fetchError'));
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setToken(null);
    
    // Clear localStorage completely
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    
    console.log('User logged out - localStorage cleared');
  };

  const updateToken = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem('authToken', newToken);
  };

  useEffect(() => {
    setAuthContextRef({ logout, updateToken });
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    updateToken,
    isLoading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
